
import * as _ from "underscore";
import { AssertExtends } from 'lib/assertExtends';

import S, { DataSignal } from 's-js';
import SArray, { SArray as ArraySignal, lift, SDataArray } from 's-array';
import nonuniqueIndex from 'lib/nonuniqueIndex';
import TZDate from "lib/TZDate";
import { tzmomentMod } from 'lib/tzmoment';
import { callAll } from "lib/callAll";
import { DraftObject } from "lib/draftSignals";
import { ValidationWatcher, Errors } from 'lib/validationWatcher';
import { ValidationRule, ValidationRules } from 'lib/clientSideValidation';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { debounceSignal } from 'lib/debounceSignal';
import { TZUtils } from 'lib/TZUtils';
import { memoize } from 'lib/memoize';
import UOM from "lib/UOM";
import { RequestQueue } from "lib/requestQueue";

import { Measurement } from 'data/models/Measurement';
import { EffectiveDate } from 'data/models/ScheduleDate';
import { PierMark } from 'data/models/PierMark';
import { PortCall } from 'data/models/PortCall';
import { BerthClosure } from 'data/models/BerthClosure';
import { BerthReservation } from 'data/models/BerthReservation';
import { BollardPosition } from "data/models/BollardPosition";
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { UnlistedBerthReservation } from 'data/models/UnlistedBerthReservation';
import { Port } from 'data/models/Port';
import { Berth } from 'data/models/Berth';
import { PierGroup } from 'data/models/PierGroup';
import { BerthCapability } from'data/models/BerthCapability';
import { BerthReservationStatus } from 'server/enums/BerthReservation';
import { PortCallRights, BerthRights, BerthClosureRights } from "server/enums/Rights";
import { UserSummary } from 'data/models/UserSummary';
import { PCCache } from 'data/PCCache';
import { Entrypoints } from 'core/app/entrypoints';
import { IAjax, Client as IAjaxClient } from 'lib/Ajax';
import { ScheduleGridResponse, ScheduleGridResponseData } from 'data/models/ScheduleGrid';
import { ScheduleGridUpdateRequestData } from 'data/models/ScheduleGridUpdateRequest';
import { ScheduleGridUpdateResponseData } from 'data/models/ScheduleGridUpdateResponse';
import { LocalizationContext } from 'common/models/LocalizationContext';
import { DataCacheUpdateEntities } from 'data/models/DataCacheUpdate';
import { VesselSide } from 'server/enums/BerthReservation';

import { BerthSelector } from 'portcall/berthselector/model';

import {
    DEFAULT_NEW_SHIFT_DURATION_MINS,
    SAME_PIERGROUP_TRANSIT_MINS,
    DIFFERENT_PIERGROUP_TRANSIT_MINS
} from 'common/services/scheduleConstants';

import { ScheduleGridCache } from './cache';
import { Typeahead } from 'portcall/typeahead/model';
import { IssueType } from "data/models/IssueType";


// TEMPORARY pixel values for sizing calculations
// TODO calculate pixel sizing in view and send info to model as needed
const MOBILE_THRESHOLD = 480; //MOBILE_THRESHOLD = @screen-size-phone-sm;
export function berthBarWidth(screenWidth: number) {
    const STANDARD_BERTH_BAR = 200,
        MOBILE_BERTH_BAR = 125;
        
    if (screenWidth <= MOBILE_THRESHOLD) {
        return MOBILE_BERTH_BAR;
    }
    return STANDARD_BERTH_BAR;
}

export enum SelectMode  {
    Details = 1,
    ChangeBerth = 2,
    EditTimeblock = 3,
    AddShift = 4,
    ViewOverlaps = 5
}

export class ScheduleGridParams {
    constructor(
        public compact : boolean,
        public date : TZDate,
        public resid : number | null,
        public uresid : number | null,
        public bclid : number | null,
        public port : string,
        public capability : string,
        public mode : SelectMode,
        public backOnComplete : boolean
    ) { }
}

// static values

const timeIncrement = 15, // minimum time increment of a drag-and-drop operation
    pierMarkIncrement = 5; // minimum footmark increment of a drag-and-drop operation;

export const ScheduleGrid = (
    params : ScheduleGridParams,
    user : () => UserSummary | null,
    tzutils : TZUtils, 
    cache : ScheduleGridCache, 
    data : PCCache,
    entrypoints : Entrypoints, 
    ajax : IAjax,
    Localization : LocalizationContext
) => {
    const CompactView = S.value(params.compact),// all days the application has seen to date
        allDays = {} as { [date : string] : DayModel },
        DateRange = new DateRangeModel(params, allDays, tzutils, CompactView);

    const model = cache.ScheduleGridResponses.update1(null, { From: S.sample(DateRange.FromDate).toJSON(), To: S.sample(DateRange.ToDate).toJSON() });

    // we'll update the model ASAP, but for the time being, seed it with all known timeline items in the date range
    model.BerthReservations(S.sample(() => Array.from(data.BerthReservations.items()).filter(r => DateRange.OverlapsPosition(timelinePositionForReservation(r)))));
    model.UnlistedReservations(S.sample(() => Array.from(data.UnlistedBerthReservations.items()).filter(ur => DateRange.OverlapsPosition(timelinePositionForUnlistedReservation(ur)))));
    model.BerthClosures(S.sample(() => Array.from(data.BerthClosures.items()).filter(bcl => DateRange.OverlapsPosition(timelinePositionForBerthClosure(bcl)))));

    const CurrentTool = S.value<ITimelineItemTool | null>(null);

    const
        reservationSets = SArray<() => BerthReservation[]>([model.BerthReservations]),
        uniqueReservationIds = () => uniqueIds(reservationSets, data.BerthReservations.index),
        reservationMap = memoize((id : number) => ReservationModel(id, ajax, data, model.PortCallRights)),
        reservations = lift(S(() => uniqueReservationIds().map(reservationMap))),
        unlistedReservationSets = SArray<() => UnlistedBerthReservation[]>([model.UnlistedReservations]),
        uniqueUnlistedReservationIds = () => uniqueIds(unlistedReservationSets, data.UnlistedBerthReservations.index),
        unlistedReservationMap = memoize((id : number) => UnlistedReservationModel(id, data)),
        unlistedReservations = lift(S(() => uniqueUnlistedReservationIds().map(unlistedReservationMap))),
        berthClosureSets = SArray<() => BerthClosure[]>([model.BerthClosures]),
        uniqueBerthClosureIds = () => uniqueIds(berthClosureSets, data.BerthClosures.index),
        addClosureBerths = lift(() => Array.from(data.Berths.items()))
            .filter(b => (model.BerthRights()[b.Id()] & BerthRights.AddClosure) !== 0),
        berthClosureMap = memoize((id : number) => BerthClosureModel(id, ajax, data, model.BerthClosureRights, addClosureBerths)),
        berthClosures = lift(S(() => uniqueBerthClosureIds().map(berthClosureMap))),
        timelineItems = (reservations as ArraySignal<TimelineItem>)
            .concat(unlistedReservations)
            .concat(berthClosures),
        // Index timeline items by PierGroup for constructing our PierGroupModels. This also
        // handles the fact that we may have draft changes to a timeline item's Berth, and
        // therefore we can't use properties like PierGroup.Reservations() that are based on
        // the committed state of the world.
        piergroupTimelineItems = nonuniqueIndex(timelineItems, i => { const pos = i.TimelinePosition(); return pos.Berth() && pos.Berth()!.PierGroupId() || 0; }),
        piergroupMap = memoize((pg : PierGroup) => new PierGroupModel(pg, piergroupTimelineItems(S.sample(pg.Id))
            .filter(i => DateRange.OverlapsPosition(i.TimelinePosition()))
            .sort(timelineItemDisplayOrder)
        )),
        portMap = memoize((port : Port) => new PortModel(port, port.PierGroups.orderBy(pg => pg.Order()).map(piergroupMap))),
        Ports = S(() => Array.from(data.Ports.items()).sort((a, b) => a.Order() - b.Order()).map(portMap));

    // make sure current DateRange contains the given timeline item
    const PanToTimelinePosition = (pos : ITimelinePosition) => {
            if (pos.Start() && pos.End() && (pos.Start()! > DateRange.ToDate() || pos.End()! < DateRange.FromDate())) {
                DateRange.PanToViewDate(pos.Start()!);
            }
        },
        PanToTimelineItem = (i : TimelineItem) => PanToTimelinePosition(i.TimelinePosition());

    const
        // Clock for determining "... ago" text in overlaps popups
        overlapsClock = S(() => {
            const tool = CurrentTool();
            return (
                tool instanceof ReservationOverlapsTool ||
                tool instanceof BerthClosureOverlapsTool ||
                null
            ) && tzutils.clock(60 * 1000);
        }),
        overlapsNow = () => overlapsClock() ? overlapsClock()!.now() : tzutils.now();

    const
        ChangeTool : IChangeTool = {
            None()                                                    { CurrentTool(null); },
            ReservationDetails(r : BerthReservation)                  { const _r = reservationMap(r.Id());           PanToTimelineItem(_r);   CurrentTool(new ReservationDetailsTool(_r, ChangeTool)); },
            UnlistedReservationDetails(ur : UnlistedBerthReservation) { const _ur = unlistedReservationMap(ur.Id()); PanToTimelineItem(_ur);  CurrentTool(new UnlistedReservationDetailsTool(_ur, ChangeTool)); },
            BerthClosureDetails(bcl : BerthClosure)                   { const _bcl = berthClosureMap(bcl.Id());      PanToTimelineItem(_bcl); CurrentTool(new BerthClosureDetailsTool(_bcl, ChangeTool)); },
            ChangeBerth(r : BerthReservation)                         { const _r = reservationMap(r.Id());           PanToTimelineItem(_r);   CurrentTool(new ChangeBerthTool(_r, piergroupMap, ChangeTool)); },
            EditTimeblock(r : BerthReservation)                       { const _r = reservationMap(r.Id());           PanToTimelineItem(_r);   CurrentTool(new EditTimeblockTool(_r, ChangeTool, tzutils)); },
            AddShift(r : BerthReservation)                            { const _r = reservationMap(r.Id());           PanToTimelineItem(_r);   CurrentTool(new AddShiftTool(r.PortCall()!, ChangeTool, reservationSets, ajax, entrypoints, tzutils)); },
            ReservationOverlaps(r : BerthReservation)                 { const _r = reservationMap(r.Id());           PanToTimelineItem(_r);   CurrentTool(new ReservationOverlapsTool(_r, overlapsNow, data, ChangeTool)); },
            BerthClosureOverlaps(bcl : BerthClosure)                  { const _bcl = berthClosureMap(bcl.Id());      PanToTimelineItem(_bcl); CurrentTool(new BerthClosureOverlapsTool(_bcl, overlapsNow, data, ChangeTool)); }
        },
        Validation = () => {
            const tool = CurrentTool();
            return tool && (tool.Validation ? tool.Validation() : null) || {} as Errors;
        },
        ShowErrorModal = S.data(false);

    // TH 02/14/2017 -- Show error modal on validation errors
    S(() => ShowErrorModal(Object.keys(Validation()).length > 0));

    let nextScrollToToolReqId = 0;
    const
        ScrollToToolReqId = S.value<number | null>(null),
        RequestScrollToCurrentTool = () => ScrollToToolReqId(nextScrollToToolReqId++);

    // If a new tool is selected, request scrolling to it.
    S(() => {
        if (CurrentTool()) RequestScrollToCurrentTool();
    });

    // Handle accepting/rejecting proposed date range changes by asking the current tool (if any) whether it
    // wants to stay in view.
    S(() => {
        const
            tool = CurrentTool(),
            pos = tool?.TimelinePosition?.() ?? tool?.TimelineItem?.TimelinePosition();

        // First off, do we have a date discrepancy, meaning the proposed date range fails to overlap the
        // selected item?
        if (pos?.Start() && pos.End() && !DateRange.ProposalOverlapsPosition(pos)) {
            S.sample(() => S.freeze(() => {
                // Yes, we have a date discrepancy. Test whether rejecting the proposed dates would be enough to
                // keep the selected item in view....
                if (DateRange.OverlapsPosition(pos)) {
                    // Yes, rejecting the proposed dates would keep the current item in view. Ask the tool if
                    // it wants to stay selected or not....
                    const newTool = tool?.OnDateDiscrepancy?.() ?? null;
                    if (tool === newTool) {
                        // Yup, the tool wants to stay selected, so reject the proposed dates and give users some
                        // UI feedback about the rejection.
                        DateRange.RejectProposedDates();
                        RequestScrollToCurrentTool();
                    } else {
                        // The current tool offered us another tool (or null) to select instead. Accept the
                        // proposed dates and select that new tool (or null).
                        DateRange.AcceptProposedDates();
                        CurrentTool(newTool);
                    }
                } else {
                    // No, rejecting proposed dates will NOT keep the current item in view. This means the date
                    // discrepancy must involve some change to the selected item's timeline position, not just
                    // a change to the proposed date range. Pan to keep the selected item in view, to provide
                    // clear feedback about the change in timeline position.
                    DateRange.PanToOverlapPosition(pos);
                }
            }));
        } else {
            // No date discrepancy. Just accept the proposed dates.
            DateRange.AcceptProposedDates();
        }
    });

    // set port, piergroup and reservation .Tool() signals when the tool applies to them
    S(() => {
        let tool = CurrentTool(),
            berth = () => tool?.TimelinePosition ? tool.TimelinePosition().Berth() : tool?.TimelineItem ? tool.TimelineItem.TimelinePosition().Berth() : null,
            tpg = tool && tool.PierGroup ? tool.PierGroup() : berth() && berth()!.PierGroup(),
            ti = tool && tool.TimelineItem;
        for (let port of Ports()) {
            let portHasTool = false;
            for (let pg of port.PierGroups()) {
                let pgHasTool = pg.PierGroup === tpg;
                portHasTool = portHasTool || pgHasTool;
                pg.Tool(pgHasTool ? tool : null);
                for (let i of pg.TimelineItems()) {
                    if (i.Type === TimelineItemType.BerthReservation) {
                        i.Tool(i === ti ? tool : null);
                    }
                }
            }
            port.Tool(portHasTool ? tool : null);
        }
    });

    const
        CanAddBerthClosures = S(() => addClosureBerths().length > 0),
        ShowAddBerthClosure = S.value(false),
        AddBerthClosure = S(() => ShowAddBerthClosure() ?
            new AddBerthClosureModel(
                addClosureBerths,
                data,
                ajax,
                entrypoints,
                tzutils,
                berthClosureSets,
                model.BerthClosureRights,
                ChangeTool,
                () => ShowAddBerthClosure(false)
            ) :
            null
        );

    // start watching to see if our desiredReservationId shows up, if set
    if (params.resid) watchForDesiredReservationId();
    if (params.uresid) watchForDesiredUnlistedReservationId();
    if (params.bclid) watchForDesiredBerthClosureId();

    // instantiate a DataLoader which will incrementally update our data model with necessary new pieces of information
    DataLoader(DateRange, model, reservationSets, unlistedReservationSets, berthClosureSets, ajax, cache, entrypoints);

    const PortFilter = PortFilterModel(params, Ports),
        CapabilityFilter = CapabilityFilterModel(params, lift(() => Array.from(data.BerthCapabilities.items())));

    S(() => Ports().forEach(port => port.IsDisplayed(PortFilter.QueryMatches(port.Port))));
    S(() => Ports().forEach(port => port.PierGroups().forEach(pg => pg.IsDisplayed(CapabilityFilter.QueryMatches(pg.PierGroup)))));

    const showActionsMenu = S.data(false);

    return {
        DateRange,
        PortFilter,
        CapabilityFilter,
        Ports,
        CurrentTool,
        Validation,
        ShowErrorModal,
        ScrollToToolReqId,
        ShowAddBerthClosure,
        AddBerthClosure,
        CompactView,
        Localization,
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu,
        ToggleCompactView,
        MenuAction,
        OpenSpaceClick,
        TimelineItemClick,
        CanReserveBerths,
        CanManageBerths,
        CanAddBerthClosures,
        User: user,
        TZUtils: tzutils // aah: so much in the view works on dates that I'm exposing tzutils here instead of passing it through all the view props
    };

    function ToggleCompactView() {
        CompactView(!CompactView());
    }
    
    function ToggleActionsMenu() {
        showActionsMenu(!showActionsMenu());
    }

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            showActionsMenu(false);
        });
    }

    // watch reservations() to see if the reservation with the desired id shows up and, if so, select it and scroll to it
    function watchForDesiredReservationId() {
        const desiredReservation = data.BerthReservations.index(params.resid!);

        S(found => {
            if (!found && desiredReservation()) {
                const dres = desiredReservation()!;
                if (params.mode === SelectMode.ChangeBerth) ChangeTool.ChangeBerth(dres);
                else if (params.mode === SelectMode.EditTimeblock) ChangeTool.EditTimeblock(dres);
                else if (params.mode === SelectMode.AddShift) ChangeTool.AddShift(dres);
                else if (params.mode === SelectMode.ViewOverlaps) ChangeTool.ReservationOverlaps(dres);
                else ChangeTool.ReservationDetails(dres);
                return true;
            } else {
                return false;
            }
        }, false);
    }

    // Same idea, but for unlisted reservations
    function watchForDesiredUnlistedReservationId() {
        const desiredUnlistedReservation = data.UnlistedBerthReservations.index(params.uresid!);

        S(found => {
            if (!found && desiredUnlistedReservation()) {
                const ur = desiredUnlistedReservation()!;
                ChangeTool.UnlistedReservationDetails(ur);
                return true;
            } else {
                return false;
            }
        }, false);
    }

    // Same idea, but for berth closures
    function watchForDesiredBerthClosureId() {
        const desiredBerthClosure = data.BerthClosures.index(params.bclid!);

        S(found => {
            if (!found && desiredBerthClosure()) {
                const bcl = desiredBerthClosure()!;
                if (params.mode === SelectMode.ViewOverlaps) ChangeTool.BerthClosureOverlaps(bcl);
                else ChangeTool.BerthClosureDetails(bcl);
                return true;
            } else {
                return false;
            }
        }, false);
    }

    function OpenSpaceClick(loc : GridLocation) {
        let tool = CurrentTool();
        if (tool === null) tool = NewReservationTool.TryCreateTool(loc, model.BerthRights(), tzutils, piergroupMap);
        else tool = tool.OpenSpaceClick?.(loc) ?? null;
        CurrentTool(tool);
    }

    function TimelineItemClick(item : TimelineItem) {
        let tool = CurrentTool();
        if (tool === null || !tool.TimelineItemClick) tool = item.MakeDetailsTool(ChangeTool);
        else tool = tool.TimelineItemClick(item);
        CurrentTool(tool);
    }

    // display order is important b/c later reservations overlap earlier ones in the dislay
    function timelineItemDisplayOrder(a : TimelineItem, b : TimelineItem) {
        return (CompactView() ? 0 : (timelineItemDisplayGroup(a) - timelineItemDisplayGroup(b)))
            || timelineItemChronologicalOrder(a, b);
    }

    function timelineItemDisplayGroup(item : TimelineItem) {
        const tool = CurrentTool();
        return (
            tool && tool.TimelineItem === item ? 6 :
                item.Type === TimelineItemType.BerthReservation ?
                    item.Reservation.Status() === BerthReservationStatus.Booked ? 5 :
                    item.Reservation.Status() === BerthReservationStatus.Requested ? 4 :
                    item.Reservation.Status() === BerthReservationStatus.Conflicted ? 3 :
                    2 :
                item.Type === TimelineItemType.BerthClosure ? 1 :
                0
        );
    }

    function timelineItemChronologicalOrder(a : TimelineItem, b : TimelineItem) {
        const aPos = a.TimelinePosition(), bPos = b.TimelinePosition();
        return (
            aPos.Start() && bPos.Start() ?
                aPos.Start()! < bPos.Start()! ? -1
              : aPos.Start()! > bPos.Start()! ? 1
              : aPos.End  ()! < bPos.End  ()! ? -1
              : aPos.End  ()! > bPos.End  ()! ? 1
              : 0
          : 0
        );
    }

    function CanReserveBerths() {
        return model.CanReserveBerths();
    }

    function CanManageBerths() {
        return model.CanManageBerths();
    }
}

// the DataLoader incrementally loads new data into the model as the range of displayed days changes
function DataLoader(
    dateRange : DateRangeModel,
    model : ScheduleGridResponse,
    reservationSets : SDataArray<() => BerthReservation[]>,
    unlistedReservationSets : SDataArray<() => UnlistedBerthReservation[]>,
    berthClosureSets : SDataArray<() => BerthClosure[]>,
    ajax : IAjax,
    cache : ScheduleGridCache,
    entrypoints : Entrypoints
) {
    var requested = {} as { [day : string] : boolean },
        loaded = {} as { [day : string] : boolean };

    S.sample(loadInitialData);
    S.on(debounceSignal(dateRange.Days, 300), watchForDataUpdates, undefined, true);

    // make our initial request for data
    function loadInitialData() {
        var initialDays = dateRange.Days(),
            url = entrypoints.ScheduleGrid({ From: dateRange.FromDate().toJSON(), To: dateRange.ToDate().toJSON() });

        initialDays.forEach(function (d) { requested[d.Day.toJSON()] = true; });

        let req = ajax.Url<ScheduleGridResponseData>(url).Get();
        return req.then(json => {
            S.freeze(() => {
                cache.ScheduleGridResponses.update1(req.Date, json);
                
                initialDays.forEach(function (d) {
                    d.IsLoaded(true);
                    loaded[d.Day.toJSON()] = true;
                });
            });
        });
    }

    // watch the days array, and check that data has been loaded for all days in it
    function watchForDataUpdates() {
        var need = {} as { [day : string] : DayModel };

        dateRange.Days().forEach(function (day) {
            var key = day.Day.toJSON();
            if (loaded[key]) {
                day.IsLoaded(true);
            } else if (requested[key]) {
                // nothing to do, request still pending
            } else {
                need[key] = day;
            }
        });

        if (Object.keys(need).length) {
            // we need to tell the server what we need and what we've already requested so that it can send us the missing items
            const req = ajax.Url<ScheduleGridUpdateRequestData, ScheduleGridUpdateResponseData>(entrypoints.UpdateScheduleGrid).Post({
                Need: Object.keys(need),
                Have: Object.keys(requested)
            });

            req.then(json => {
                model.PortCallRights(Object.assign(model.PortCallRights(), json.PortCallRights));
                model.BerthClosureRights(Object.assign(model.BerthClosureRights(), json.BerthClosureRights));

                const
                    update = cache.ScheduleGridUpdateResponses.update1(req.Date, json),
                    reses = update.BerthReservations(),
                    urs = update.UnlistedReservations(),
                    bcls = update.BerthClosures();

                // note: can't push update.BerthReservations onto reservationSets, since ScheduleGridUpdateResponses don't have a key,
                // so if we push the signal onto the set, it will be reset to the next update once it comes in
                reservationSets.push(() => reses);
                // And ditto for other timeline items
                unlistedReservationSets.push(() => urs);
                berthClosureSets.push(() => bcls);

                // mark the days as loaded
                for (let key in need) {
                    need[key].IsLoaded(true);
                    loaded[key] = true;
                }
            });

            for (let key in need) {
                requested[key] = true;
            }
        }
    }
}

export class DayModel {
    IsLoaded = S.value(false);
    constructor(
        public Day : TZDate,
        public NextDay : TZDate,
        public Today : boolean
    ) { }
}

export class PortModel {
    IsDisplayed = S.value(true);
    Tool = S.value<ITimelineItemTool | null>(null);
    constructor(
        public Port : Port,
        public PierGroups : () => PierGroupModel[]
    ) {}
}

export class PierGroupModel {
    readonly IsDisplayed = S.value(true);
    readonly Tool = S.value<ITimelineItemTool | null>(null);
    readonly Berths : () => Berth[];
    readonly Bollards : () => BollardPosition[];
    readonly SelectedBollard = S.value<BollardPosition | null>(null);
    constructor(
        readonly PierGroup: PierGroup,
        readonly TimelineItems: () => TimelineItem[]
    ) { 
        this.Berths = this.PierGroup.Berths.sort(Berth.StartMarkOrder);
        this.Bollards = S(() => {
            const bollards = PierGroup.Berths().reduce(
                (acc, next) => acc.concat(next.BollardPositions()),
                [] as BollardPosition[]
            );
            bollards.sort(BollardPosition.PierMarkOrder);
            return bollards;
        });
    }

    SelectBollard(bol : BollardPosition) {
        this.SelectedBollard(bol);
    }

    DeselectBollard(bol : BollardPosition) {
        if (S.sample(this.SelectedBollard) === bol) this.SelectedBollard(null);
    }
}

export class DateRangeModel {
    readonly FromDate : () => TZDate;
    readonly ToDate : () => TZDate;
    // Date changes go through a workflow of being proposed and then being either accepted or rejected depending
    // on whether they're compatible w/ the current tool, if any.
    readonly ProposedFromDate : DataSignal<TZDate>;
    readonly ProposedToDate : () => TZDate;
    readonly MaxDuration : DataSignal<number>;
    readonly Duration : () => number;
    readonly Days : () => DayModel[];
    readonly Now : TZDate;
    readonly Today : TZDate;

    // Same as FromDate, but writeable
    private readonly _FromDate : DataSignal<TZDate>;

    constructor(params : ScheduleGridParams, allDays: { [ date : string ] : DayModel }, private readonly tzutils : TZUtils, private readonly CompactView : () => boolean) {
        this._FromDate = S.value(params.date, TZDate.eq);
        this.FromDate = () => this._FromDate();
        this.ProposedFromDate = S.value(params.date, TZDate.eq);
        const msPerDay = 24 * 60 * 60 * 1000;
        this.MaxDuration = S.value(
            window.innerWidth <= MOBILE_THRESHOLD ? 
                msPerDay :
                (window.innerWidth - berthBarWidth(window.innerWidth)) / 240 * msPerDay
        );
        this.Duration = S(() => CompactView() ? msPerDay : this.MaxDuration());
        this.ToDate = S(() => tzutils.newDate(this.FromDate().getTime() + this.Duration()));
        this.ProposedToDate = S(() => tzutils.newDate(this.ProposedFromDate().getTime() + this.Duration()));
        this.Now = tzutils.now();
        this.Today = tzutils.today();
        this.Days = S(() => {
            var day : TZDate, 
                days = [] as DayModel[], 
                dayModel : DayModel,
                nextDay = (d : TZDate) => tzmomentMod(tzutils, d, m => m.add(1, 'day'));

            for (day = this.FromDate(); day < this.ToDate(); day = nextDay(day)) {
                // get cached day if we have it, otherwise create one and cache it
                dayModel = allDays[day.toJSON()] || (allDays[day.toJSON()] = new DayModel(day, nextDay(day), TZDate.eq(day, this.Today)));
                days.push(dayModel);
            }

            return days;
        });
    }

    // move the displayed date range the requested number of days, positive (future) or negative (past)
    ShiftDate(days : number) {
        const from = tzmomentMod(this.tzutils, this.FromDate(), m => m.add(days, 'day'));

        this.ProposedFromDate(from);
    }

    GoToToday() {
        this.ProposedFromDate(this.tzutils.today());
    }

    // pan the view to bring the given date into view
    PanToViewDate(d : TZDate) {
        let shift = 0;
        const extraDays = this.CompactView() ? 0 : 1;

        if (d < this.FromDate()) shift = Math.floor((+d - +this.FromDate()) / 24 / 60 / 60 / 1000) - extraDays;
        else if (d > this.ToDate()) shift = Math.ceil((+d - +this.ToDate()) / 24 / 60 / 60 / 1000) + extraDays;

        if (shift !== 0) this.ShiftDate(shift);
    }

    PanToOverlapPosition(pos : ITimelinePosition) {
        if (pos.Start() && pos.End()) {
            let shift = 0;
            const extraDays = this.CompactView() ? 0 : 1;

            if (pos.End()! < this.FromDate()) shift = Math.floor((+pos.End()! - +this.FromDate()) / 24 / 60 / 60 / 1000) - extraDays;
            if (pos.Start()! > this.ToDate()) shift = Math.ceil((+pos.Start()! - +this.ToDate()) / 24 / 60 / 60 / 1000) + extraDays;

            if (shift !== 0) this.ShiftDate(shift);
        }
    }

    OverlapsPosition(pos : ITimelinePosition) {
        return !!pos.Start() && !!pos.End() && pos.Start()! <= this.ToDate() && pos.End()! >= this.FromDate();
    }

    ProposalOverlapsPosition(pos : ITimelinePosition) {
        return !!pos.Start() && !!pos.End() && pos.Start()! <= this.ProposedToDate() && pos.End()! >= this.ProposedFromDate();
    }

    AcceptProposedDates() {
        this._FromDate(this.ProposedFromDate());
    }

    RejectProposedDates() {
        this.ProposedFromDate(this.FromDate());
    }
}

// TH 03/28/2017 -- Describes the position
export interface ITimelinePosition {
    Start : () => TZDate | null;
    // NOTE: If both Start and End are populated, then End MUST be on or after Start. It's up to implementers
    // of this interface to guarantee this. Consumers can do whatever they want if this is violated -- throw
    // an exception, render nonsensical things, etc.
    End : () => TZDate | null;
    Berth : () => Berth | null;
    PierMark : PierMark;
    Length : () => Measurement | null;
    // TH 03/29/2017 -- Must be unique across all possible items
    // Note that this is in fact part of the position, since
    // it's used by the view when translating coordinates in compact view.
    UniqueKey : () => string;
}

function timelinePositionForReservation(res : BerthReservation) : ITimelinePosition {
    return {
        Start: () => res.Status() === BerthReservationStatus.Cancelled ? null : res.Itinerary()?.Arrival.Effective() ?? null,
        End:   () => res.Status() === BerthReservationStatus.Cancelled ? null : res.Itinerary() ? maxDate(res.Itinerary()!.Arrival.Effective(), res.Itinerary()!.Departure.Effective()) : null,
        Berth: () => res.Berth(),
        PierMark: res.PierMark,
        Length: () => res.Length,
        UniqueKey: () => 'res-' + res.Id()
    };
}

function timelinePositionForUnlistedReservation({ Start, End, Berth, PierMark, Length, Id } : UnlistedBerthReservation) : ITimelinePosition {
    return {
        Start,
        End: () => maxDate(Start(), End()),
        Berth,
        PierMark,
        Length: () => Length,
        UniqueKey: () => 'ures-' + Id()
    };
}

const VERY_LONG_TIME_IN_THE_FUTURE = new TZDate(Date.now() + 2000 * 365.25 * 24 * 60 * 60 * 1000);
function timelinePositionForBerthClosure({ StartDate, EndDate, Berth, PierMark, Length, Id } : BerthClosure) : ITimelinePosition {
    return {
        Start: StartDate,
        // Null EndDate is the same as "closed indefinitely," which we'll interpret as "roughly 2000 yrs from now." :)
        End: () => EndDate() ? maxDate(StartDate(), EndDate()!) : VERY_LONG_TIME_IN_THE_FUTURE,
        Berth,
        PierMark,
        Length: () => Length,
        UniqueKey: () => 'bcl-' + Id()
    };
}

function maxDate(d1 : TZDate, d2 : TZDate) {
    return d1 < d2 ? d2 : d1;
}

export interface IFootmarkSelection {
    Footmark : Measurement;
    Position : ITimelinePosition;
}

function footmarkSelectionForReservation(res : BerthReservation) : IFootmarkSelection {
    return {
        Footmark: res.Footmark,
        Position: timelinePositionForReservation(res)
    };
}

function footmarkSelectionForUnlistedReservation(ur : UnlistedBerthReservation) : IFootmarkSelection {
    return {
        Footmark: ur.Footmark,
        Position: timelinePositionForUnlistedReservation(ur)
    };
}

function footmarkSelectionForBerthClosure(bcl : BerthClosure) : IFootmarkSelection {
    return {
        Footmark: bcl.Footmark,
        Position: timelinePositionForBerthClosure(bcl)
    };
}

export const enum TimelineItemType {
    BerthReservation = 'BR',
    UnlistedReservation = 'UR',
    BerthClosure = 'BCL'
}

// Common interface that all timeline items should implement. This type mainly exists to
// bring consistency to the various timeline item implementations. Calling code should
// almost always use the TimelineItem type below, which satisfies this interface but also
// allows type narrowing by testing the Type property.
interface ITimelineItem {
    Type : TimelineItemType;
    TimelinePosition() : ITimelinePosition;
    FootmarkSelection() : IFootmarkSelection;
    MakeDetailsTool(changeTool : IChangeTool) : ITimelineItemTool | null;
}

/**
 * An item displayed on the BerthTimeline.
 */
export type TimelineItem = BerthReservationModel | UnlistedReservationModel | BerthClosureModel;

export type BerthReservationModel = AssertExtends<ReturnType<typeof ReservationModel>, ITimelineItem>;
function ReservationModel(id : number, ajax : IAjax, data : PCCache, portCallRights : () => { [id : number] : PortCallRights }) {
    const
        res = data.BerthReservations.update1(null, { Id: id }),
        client = ajax.Url<BerthReservation>(() => res.Url() || '', { useCache: true, include: [ res => res.Itinerary ] }),
        { object: Reservation, methods: draftMethods } = DraftObject(res, ['Footmark.Num']),
        pt = data.ItineraryPoints.update1(null, { Id: id }),
        { object: ItineraryPoint, methods: ptDraftMethods } = DraftObject(pt, ['Arrival.Estimated', 'Arrival.Actual', 'Departure.Estimated', 'Departure.Actual', 'LocationId']),
        Validation = ValidationWatcher(client),
        Tool = S.value<ITimelineItemTool | null>(null),
        rights         = () => portCallRights()[ItineraryPoint.PortCallId()],
        Edit           = () => (rights() & PortCallRights.Edit) !== 0,
        EditActuals    = () => (rights() & PortCallRights.EditActuals) !== 0,
        commitQueue = new RequestQueue(async () => {
            if (draftMethods.isCaching() || ptDraftMethods.isCaching()) {
                const
                    resCheckpoint = draftMethods.checkpoint(),
                    ptCheckpoint = ptDraftMethods.checkpoint();
                await client.Put(Reservation);
                S.freeze(() => {
                    // the DataCache will have committed changes to the model already, so we actually revert() here, not commit().
                    // otherwise, any tweaks to the data by the server won't be picked up.
                    resCheckpoint.revert();
                    ptCheckpoint.revert();
                });
            }
        });

    // have to replace PierMark in draft res, as the base PierMark is bound to the base signals
    Reservation.PierMark = new PierMark(Reservation.Footmark, () => Reservation.Berth());

    // have to recreate Effective dates, so that they read from the draft signals
    ItineraryPoint.Arrival.Effective = EffectiveDate(ItineraryPoint.Arrival);
    ItineraryPoint.Departure.Effective = EffectiveDate(ItineraryPoint.Departure);

    // have to re-attach ItineraryPoint's reference to Location so that it reads from draft LocationId
    Reservation.Itinerary = S.data(ItineraryPoint);
    ItineraryPoint.Location = S.data(S.sample(pt.Location));
    data.ItineraryPoints.attach(ItineraryPoint, [ItineraryPoint.Location]);

    // Apply most-utilized-berth rule. Matters for, e.g., selecting the correct length/time buffers when moving
    // reservations around in a piergroup in which different berths use different length or time buffers.
    ApplyBerthReservationMostUtilizedBerthRule(Reservation, ItineraryPoint, () => draftMethods.isCaching() || ptDraftMethods.isCaching());

    return {
        // ITimelineItem impl...
        Type: TimelineItemType.BerthReservation as const,
        TimelinePosition() { return timelinePositionForReservation(Reservation); },
        FootmarkSelection() { return footmarkSelectionForReservation(Reservation); },
        MakeDetailsTool(changeTool : IChangeTool) { return new ReservationDetailsTool(this, changeTool); },

        // Other properties...
        Reservation,
        ItineraryPoint,
        // Issues representing overlaps that should be displayed in the overlaps popup
        Overlaps: Reservation.Issues.filter(i =>
            i.Type() === IssueType.ReservationOverlapsOther ||
            i.Type() === IssueType.ReservationOverlapsShift ||
            i.Type() === IssueType.ReservationOverlapsBerthClosure
            // NOT ReservationOverlapsVesselSchedule. That indicates a logical overlap (same
            // vessel assigned to two PortCalls simultaneously). Whereas what we care about
            // here is physical overlaps (two things occupying the same berth space at the
            // same time).
        ),
        Validation,
        Tool,
        Edit,
        EditActuals,
        CommitChanges,
        RevertChanges
    };

    function CommitChanges() {
        commitQueue.enqueueRequest();
        return commitQueue.mostRecentRequest();
    }

    function RevertChanges() {
        Validation({});
        //ItineraryValidation({});
        draftMethods.checkpoint().revert();
        ptDraftMethods.checkpoint().revert();
    }
}

function ApplyBerthReservationMostUtilizedBerthRule(res : BerthReservation, pt : ItineraryPoint, isCaching : () => boolean) {
    // Refine selected berth as footmarks or selected berth change. This is purely for better client-side
    // feedback. The server applies its own version of the same logic.
    S(() => {
        // Similar to berth closures below, we wanna avoid modifying BerthId unless we've gotten some kind of
        // explicit user input.
        if (!isCaching()) return;
        // Similar to berth closures below, also skip updating if Location and LocationId are out of sync.
        if (pt.Location()?.Id() !== pt.LocationId()) return;

        const pg = pt.Location()?.Berth()?.PierGroup() ?? null;
        if (pg) {
            const b = pg.MostUtilizedBerth(res.Footmark, res.Length);
            if (b) pt.LocationId(b.Id());
        }
    });
}

export type UnlistedReservationModel = AssertExtends<ReturnType<typeof UnlistedReservationModel>, ITimelineItem>;
function UnlistedReservationModel(id : number, data : PCCache) {
    const
        // No draft object b/c unlisted reservations aren't ever editable.
        UnlistedReservation = data.UnlistedBerthReservations.update1(null, { Id: id });

    return {
        // ITimelineItem impl...
        Type: TimelineItemType.UnlistedReservation as const,
        TimelinePosition() { return timelinePositionForUnlistedReservation(UnlistedReservation); },
        FootmarkSelection() { return footmarkSelectionForUnlistedReservation(UnlistedReservation); },
        MakeDetailsTool(changeTool : IChangeTool) { return new UnlistedReservationDetailsTool(this, changeTool); },

        // Other properties...
        UnlistedReservation
    };
}

export type BerthClosureModel = AssertExtends<ReturnType<typeof BerthClosureModel>, ITimelineItem>;
function BerthClosureModel(id : number, ajax : IAjax, data : PCCache, berthClosureRights : () => { [id : number] : BerthClosureRights }, addClosureBerths : () => Berth[]) {
    const
        closure = data.BerthClosures.update1(null, { Id: id }),
        client = ajax.Url<BerthClosure>(() => closure.Url() || '', { useCache: true }),
        { object: BerthClosure, methods: draftMethods } = DraftObject(closure, [
            'Title',
            'StartDate',
            'EndDate',
            'BerthId',
            'Footmark.Num',
            'Footmark.UOM',
            'Length.Num',
            'Length.UOM',
            'Remarks'
        ]),
        { Validation, ValidForSubmit } = BerthClosureValidationWatcher(BerthClosure, true),
        rights  = () => berthClosureRights()[BerthClosure.Id()],
        Edit    = () => (rights() & BerthClosureRights.Edit) !== 0,
        commitQueue = new RequestQueue(async () => {
            if (draftMethods.isCaching()) {
                const checkpoint = draftMethods.checkpoint();
                await client.Put(BerthClosure);
                // As w/ ReservationModel, we actually revert() here not commit(), since we'll
                // let the data cache commit changes to our cached model, and we don't wanna
                // clobber the data caches commit and lose any server-side tweaks.
                checkpoint.revert();
            }
        });

    // Have to re-attach parent navigation properties to read from draft foreign keys
    BerthClosure.Berth = S.data(S.sample(closure.Berth));
    data.BerthClosures.attach(BerthClosure, [BerthClosure.Berth]);

    const berthSelector = BerthClosureBerthSelector(BerthClosure, addClosureBerths);

    ApplyBerthClosureMostUtilizedBerthRule(BerthClosure, draftMethods.isCaching);

    // DON'T have to recreate PierMark for BerthClosures. We made that property a getter
    // to avoid needing this.

    const { StartmarkNum, EndmarkNum } = BerthClosureStartAndEndMarks(BerthClosure);

    return {
        // ITimelineItem impl...
        Type: TimelineItemType.BerthClosure as const,
        TimelinePosition() { return timelinePositionForBerthClosure(BerthClosure); },
        FootmarkSelection() { return footmarkSelectionForBerthClosure(BerthClosure); },
        MakeDetailsTool(changeTool : IChangeTool) { return new BerthClosureDetailsTool(this, changeTool); },

        // Other properties...
        BerthClosure,
        StartmarkNum,
        EndmarkNum,
        // Issues representing overlaps that should be displayed in the overlaps popup.
        // Both this closure's issues and issues that any berth reservations might have
        // b/c of this closure.
        // NOTE: Keep in sync w/ portcall/berthclosuresdashboard/model (see BerthClosureViewModel)
        Overlaps: BerthClosure.Issues.filter(i =>
            i.Type() === IssueType.BerthClosureOverlapsOther
        ).concat(BerthClosure.ReservationIssues.filter(i =>
            i.Type() === IssueType.ReservationOverlapsBerthClosure
        )),
        Edit,
        Delete,
        BerthSelector: berthSelector,
        HasUnsavedChanges,
        Validation,
        ValidForSubmit,
        CommitChanges,
        RevertChanges
    };

    function HasUnsavedChanges() {
        return draftMethods.isCaching();
    }

    function CommitChanges() {
        commitQueue.enqueueRequest();
        return commitQueue.mostRecentRequest();
    }

    function RevertChanges() {
        draftMethods.checkpoint().revert();
    }

    function Delete() {
        return client.Delete();
    }
}

export class AddBerthClosureModel {
    readonly BerthClosure : BerthClosure;
    readonly StartmarkNum : DataSignal<number>;
    readonly EndmarkNum : DataSignal<number>;
    readonly BerthSelector : BerthSelector;
    readonly Validation : () => Errors;
    readonly ValidForSubmit : () => boolean;
    private readonly _Submitting : DataSignal<boolean>;
    readonly Submitting : () => boolean;
    private readonly client : IAjaxClient<BerthClosure, DataCacheUpdateEntities>;

    constructor(
        addClosureBerths : () => Berth[],
        data : PCCache,
        ajax : IAjax,
        entrypoints : Entrypoints,
        tzutils : TZUtils,
        private readonly berthClosureSets : SDataArray<() => BerthClosure[]>,
        private readonly berthClosureRights : DataSignal<{ [id : number] : BerthClosureRights }>,
        private readonly changeTool : IChangeTool,
        private readonly dismiss : () => void
    ) {
        this.BerthClosure = new BerthClosure({
            Id: 0,
            StartDate: tzutils.today().toJSON(),
            EndDate: tzmomentMod(tzutils, tzutils.today(), m => m.add(1, 'days')).toJSON(),
            Footmark: { UOM: UOM.Foot },
            Length: { UOM: UOM.Foot }
        });
        data.BerthClosures.attach(this.BerthClosure, [this.BerthClosure.Berth]);

        const { StartmarkNum, EndmarkNum } = BerthClosureStartAndEndMarks(this.BerthClosure);
        this.StartmarkNum = StartmarkNum;
        this.EndmarkNum = EndmarkNum;

        this.BerthSelector = BerthClosureBerthSelector(this.BerthClosure, addClosureBerths);

        ApplyBerthClosureMostUtilizedBerthRule(this.BerthClosure, () => true);

        this.client = ajax.Url<BerthClosure, DataCacheUpdateEntities>(entrypoints.AddBerthClosure, { useCache: true });
        const { Validation, ValidForSubmit } = BerthClosureValidationWatcher(this.BerthClosure, false);
        this.Validation = Validation;
        this.ValidForSubmit = ValidForSubmit;
        this._Submitting = S.value(false);
        this.Submitting = () => this._Submitting();
    }

    Submit() {
        if (this._Submitting()) return;
        this._Submitting(true);
        this.client.Post(this.BerthClosure).then(resp => S.freeze(() => {
            const bcl = resp.BerthClosures()[0];
            this.berthClosureSets.push(() => [bcl]);
            // Assume that if you just added a closure successfully, then you can also edit
            // that closure. Otherwise we'd default to no edit rights.
            this.berthClosureRights(Object.assign(this.berthClosureRights(), { [bcl.Id()]: BerthClosureRights.Edit }));
            this.changeTool.BerthClosureDetails(bcl);
            this.dismiss();
        })).finally(() => this._Submitting(false));
    }
}

function ApplyBerthClosureMostUtilizedBerthRule(berthClosure : BerthClosure, isCaching : () => boolean) {
    // Refine selected berth as footmarks or selected berth change. This is purely for faster client-side
    // feedback. The server applies its own version of the same logic.
    S(() => {
        // We wanna avoid modifying BerthId unless we've gotten some kind of explicit user input, in case this
        // is an existing closure whose BerthId has somehow gotten out of sync with its Footmark and Length.
        // That's b/c w/ no guard here, the UX in the event that a user happens to click on such an out-of-sync
        // closure would be really bad: For berth mgrs/admins, they'd find that the details popup immediately
        // acts as if they have unsaved changes, but Quit Changes wouldn't work (it'd appear to do nothing),
        // and they'd be forced to hit Update to get out of the bad state. For non-berth mgrs, it's even worse:
        // they don't even have access to Update (or Quit Changes), so they'd be stuck viewing that closure.
        // So, say that we only apply the most-utilized-berth rule if our draft closure is already dirty for
        // some reason. That way, we at least know that THIS computation can never be the sole reason our draft
        // closure is dirty.
        if (!isCaching()) return;
        // Also skip updating if berthClosure.Berth and berthClosure.BerthId are out-of-sync. This can happen if
        // the user selects a new berth and that changes both berthClosure.BerthId and berthClosure.Footmark;
        // changing berthClosure.Footmark will trigger this computation to re-run in the next tick of S's clock,
        // but it'll take at least one extra clock tick for berthClosure.Berth to reflect the new
        // berthClosure.BerthId value. W/o this guard, we can wind up applying the most-utilized-berth rule to
        // the wrong piergroup.
        if (berthClosure.Berth()?.Id() !== berthClosure.BerthId()) return;

        const pg = berthClosure.Berth()?.PierGroup() ?? null;
        // PierGroup.MostUtilizedBerth throws on negative lengths, so bail early in that case.
        if (pg && berthClosure.Length.Num() >= 0) {
            const b = pg.MostUtilizedBerth(berthClosure.Footmark, berthClosure.Length);
            if (b) berthClosure.BerthId(b.Id());
        }
    });
}

function BerthClosureBerthSelector(berthClosure : BerthClosure, addClosureBerths : () => Berth[]) {
    const
        // A pure event signal so that we can distinguish berth changes originating from
        // a typeahead selection vs those originating from any other source (e.g. the
        // user reverts draft berth changes).
        typeaheadSelection = S.data(null),
        selectedBerth = S.data(S.sample(berthClosure.Berth));

    // If a typeahead option is selected that differs from the current berth, then update
    // the current berth and update Footmark and Length so that the closure defaults to
    // spanning the entire berth. Be careful to do this only when a typeahead option is
    // selected, not anytime the berth changes, to avoid clobbering user Footmark/Length
    // edits if the user reverts draft berth changes.
    //
    // BUGFIX: The debounceSignal call is purely to work around a behavior (arguably a bug) in
    // our version of Twitter Typeahead (currently 0.10.5). When you select an item from a
    // Twitter typeahead (at least in v0.10.5), the typeahead does two things in succession:
    // 1) Trigger the "selected" event for that typeahead, and 2) close the typeahead's
    // dropdown menu. (Search lib/typeahead.jquery.js for "_select:"). The issue for us is
    // that if selecting a berth causes the berth closure to change piergroups, then (1)
    // causes us to totally destroy the existing BerthClosure details popup, including
    // destroying the triggering berth selector typeahead, and then construct a NEW
    // BerthClosure details popup under the DOM tree for the new piergroup. All this
    // happens synchronously before (2) runs, such that by the time (2) runs, the triggering
    // typeahead has already been destroyed. The logic for closing a typeahead's dropdown
    // menu doesn't expect that typeahead to have been destroyed in the meantime, so it ends
    // up throwing an error that leaves us in a dirty state. Search lib/typeahead.jquery.js
    // for "_getCursor:" for the method that ultimately throws the error. This gets called
    // in the course of the Dropdown class's close() method. Arguably Twitter Typeahead
    // should anticipate that firing the "selected" event might trigger code that destroys
    // the triggering typeahead and just skip closing the dropdown menu if so. We could see
    // if a more recent version has fixed or just patch Twitter Typeahead ourselves. But for
    // now, delaying the berth change so that it happens AFTER Twitter Typeahead has closed
    // the triggering typeahead's dropdown menu is enough to fix the problem.
    //
    // TODO: This fix doesn't stop the Twitter typeahead from throwing OTHER errors when
    // you change to a different piergroup. One seems to be connected to cleanup the
    // typeahead tries to do when the input loses focus. Those other errors don't seem to
    // interfere w/ our desired functionality, but still might wanna try upgrading the
    // library. Note that Twitter no longer maintains this library, but there's a fork
    // by the core-js team that seems well maintained:
    // https://github.com/corejavascript/typeahead.js
    S.on(debounceSignal(typeaheadSelection, 0, () => false), () => {
        const b = selectedBerth(), curr = berthClosure.Berth();
        if (b && b !== curr) {
            berthClosure.BerthId(b.Id());
            berthClosure.Footmark.set(b.StartMark);
            berthClosure.Length.set(b.Length);
        }
    }, undefined, true);

    // Also flow the other direction: If model's Berth changes, update SelectedBerth. This
    // supports updating the displayed berth if the user reverts draft berth changes.
    S(() => selectedBerth(berthClosure.Berth()));

    return BerthSelector({
        Berths: addClosureBerths,
        SelectedBerth: (b? : Berth | null) => {
            if (b === undefined) {
                return selectedBerth();
            } else {
                return S.freeze(() => {
                    typeaheadSelection(null);
                    return selectedBerth(b);
                });
            }
        }
    });
}

function BerthClosureStartAndEndMarks(berthClosure : BerthClosure) {
    // Support presenting in UI as start/end footmarks instead of as start footmark + length.
    // NOTE: data/models/EndMark.ts is no good for us here b/c that assumes that length is
    // fixed and edits to the EndMark update the Footmark. We want just the opposite here.

    const endmarkImmutable = berthClosure.Footmark.add(berthClosure.Length);

    return {
        StartmarkNum,
        EndmarkNum
    };

    function StartmarkNum(num? : number) {
        if (num === undefined) {
            return Math.round(berthClosure.Footmark.Num());
        } else {
            return S.freeze(() => S.sample(() => {
                const diff = num - berthClosure.Footmark.Num();
                // Changing StartmarkNum changes Length in the opposite
                // direction, to preserve the appearance for users that
                // Startmark and Endmark are independent of each other.
                berthClosure.Length.Num(berthClosure.Length.Num() - diff);
                return berthClosure.Footmark.Num(num);
            }));
        }
    }

    function EndmarkNum(num? : number) {
        if (num === undefined) {
            return Math.round(endmarkImmutable.Num());
        } else {
            return S.sample(() => berthClosure.Length.Num(num - berthClosure.Footmark.Num()));
        }
    }
}

// Do all validation client-side to provide feedback before submitting rather than after.
function BerthClosureValidationWatcher(closure : BerthClosure, initApplyValidation : boolean) {
    const
        hasTitle = () => !!closure.Title(),
        hasEndAfterStart = () => closure.EndDate() === null || +closure.EndDate()! > +closure.StartDate(),
        hasPositiveLength = () => closure.Length.Num() > 0,
        hasBerth = () => !!closure.Berth(),
        // TODO: Do these piermark calculations need to be revised for the upcoming majority
        // footmark change?
        startPiermark = () => {
            const b = closure.Berth();
            return b ? closure.Footmark.Num() + b.PierMark.Num() - b.StartMark.Num() : null;
        },
        endPiermark = () => {
            const pm = startPiermark();
            return pm === null ? null : pm + closure.Length.Num();
        },
        hasNonnegativeStartPiermark = () => {
            const pm = startPiermark();
            return pm === null || pm >= 0;
        },
        hasStartPierMarkWithinPier = () => {
            const
                pm = startPiermark(),
                pg = closure.Berth() && closure.Berth()!.PierGroup();
            return pm === null || pg === null || pm <= pg.Length.Num();
        },
        hasEndPiermarkWithinPier = () => {
            const
                pm = endPiermark(),
                pg = closure.Berth() && closure.Berth()!.PierGroup();
            return pm === null || pg === null || pm <= pg.Length.Num();
        },
        applyTitleValidation = S.value(initApplyValidation),
        applyEndDateValidation = S.value(initApplyValidation),
        applyLengthValidation = S.value(initApplyValidation),
        applyBerthValidation = S.value(initApplyValidation),
        applyFootmarkValidation = S.value(initApplyValidation),
        ifThen = (condition : () => boolean, consequence : () => boolean) => () => !condition() || consequence(),
        // Can't submit until all VRs pass
        vrsForCanSubmit = new ValidationRules(
            new ValidationRule('Title', hasTitle, ''),
            new ValidationRule('EndDate', hasEndAfterStart, ''),
            new ValidationRule('Length', hasPositiveLength, ''),
            new ValidationRule('BerthId', hasBerth, ''),
            new ValidationRule('Footmark', hasNonnegativeStartPiermark, ''),
            new ValidationRule('Footmark', hasEndPiermarkWithinPier, ''),
            new ValidationRule('Length', hasEndPiermarkWithinPier, '')
        ),
        // VRs to drive validation UI. Suppress displaying errors until user edits a given field.
        vrsForDisplay = new ValidationRules(
            new ValidationRule('Title', ifThen(applyTitleValidation, hasTitle), 'Title is required'),
            new ValidationRule('EndDate', ifThen(applyEndDateValidation, hasEndAfterStart), 'End date must be after start date'),
            new ValidationRule('Length', ifThen(applyLengthValidation, hasPositiveLength), 'Footmark end must be after footmark start'),
            new ValidationRule('BerthId', ifThen(applyBerthValidation, hasBerth), 'Berth is required'),
            new ValidationRule('Footmark', ifThen(applyFootmarkValidation, hasNonnegativeStartPiermark), 'Exceeds start of pier'),
            new ValidationRule('Footmark', ifThen(applyFootmarkValidation, hasStartPierMarkWithinPier), 'Exceeds pier group maximum'),
            new ValidationRule('Length', ifThen(applyLengthValidation, hasEndPiermarkWithinPier), 'Exceeds pier group maximum')
        );

    // Apply validation to individual fields as the user edits those fields
    S.on(closure.Title, () => applyTitleValidation(true), undefined, true);
    S.on(callAll(closure.StartDate, closure.EndDate), () => applyEndDateValidation(true), undefined, true);
    S.on(callAll(closure.Footmark.Num, closure.Length.Num), () => applyLengthValidation(true), undefined, true);
    S.on(closure.Berth, () => applyBerthValidation(true), undefined, true);
    S.on(closure.Footmark.Num, () => applyFootmarkValidation(true), undefined, true);

    return {
        Validation: vrsForDisplay.validation,
        ValidForSubmit: () => Object.keys(vrsForCanSubmit.validation()).length === 0
    };
}


function berthAtPierMark(pg : PierGroup, pm : number) {
    var berths = pg.Berths();

    for (var i = 0; i < berths.length; i++) {
        var b = berths[i];

        if (b.PierMark.Num() + b.Length.Num() > pm) {
            return b;
        }
    }
    
    return null;
}

export interface ITimelineItemTool {
    TimelineItem? : TimelineItem;
    /**
     * Optional timeline position for determining whether this tool overlaps the proposed date range. If no
     * implementation is provided, the default behavior is to use the timeline position of this tool's
     * @see TimelineItem, if any, otherwise to assume this tool overlaps any proposed date range.
     */
    TimelinePosition? : () => ITimelinePosition;
    PierGroup? : () => PierGroup;
    /**
     * Called when the user clicks an open space on the timeline. Return the tool that should be selected as
     * the new current tool, including returning null to mean "dismiss the current tool" or returning this tool
     * to mean "don't change the current tool." If no implementation is provided, the default behavior is to
     * dismiss the current tool.
     */
    OpenSpaceClick? : (loc : GridLocation) => ITimelineItemTool | null;
    /**
     * Called when the user clicks a timeline item. Return the tool that should be selected as the new current
     * tool, including returning null to mean "dismiss the current tool" or returning this tool to mean
     * "don't change the current tool." If no implementation is provided, the default behavior is to construct
     * a new details tool for the clicked item and set that as the current tool.
     */
    TimelineItemClick? : (item : TimelineItem) => ITimelineItemTool | null;
    /**
     * Called when the user proposes a new date range that fails to overlap this tool's @see TimelinePosition.
     * Return the tool that should be selected as the new current tool, including returning null to mean
     * "dismiss the current tool and accept the proposed date range" or returning this tool to mean "don't
     * change the current tool and reject the proposed date range." If no implementation is provided, the
     * default behavior is to dismiss the current tool.
     */
    OnDateDiscrepancy? : () => ITimelineItemTool | null;
    Validation? : () => Errors;
}

interface IChangeTool {
    None() : void;
    ReservationDetails(r : BerthReservation) : void;
    UnlistedReservationDetails(ur : UnlistedBerthReservation) : void;
    BerthClosureDetails(bcl : BerthClosure) : void;
    ChangeBerth(r : BerthReservation) : void;
    EditTimeblock(r : BerthReservation) : void;
    AddShift(r : BerthReservation) : void;
    ReservationOverlaps(r : BerthReservation) : void;
    BerthClosureOverlaps(bcl : BerthClosure) : void;
}

export class ChangeBerthTool implements ITimelineItemTool {
    readonly Seeking = S.value(true);
    readonly AttentionGrabber = new AttentionGrabber();

    constructor(
        readonly Reservation : BerthReservationModel,
        private readonly piergroupMap : (pg : PierGroup) => PierGroupModel,
        private readonly changeTool : IChangeTool
    ) { }

    get TimelineItem() { return this.Reservation; }
    get Validation() { return this.Reservation.Validation; }

    // Given an existing reservation of set height, try and find an open slot in the given berth to put it, starting at beginning of berth and working down
    OpenSpaceClick(loc : GridLocation) {
        const
            res = this.Reservation.Reservation,
            pt = this.Reservation.ItineraryPoint,
            pg = this.piergroupMap(loc.PierGroup),
            pc = this.Reservation.Reservation.PortCall(),
            berth = berthAtPierMark(pg.PierGroup, loc.PierMark.Num());

        if (pc === null || berth === null || pg.PierGroup.Port() !== pc.Port()) return this;

        pt.LocationId(berth.Id());

        this.Seeking(false);
        
        // find all other reservations in the piergroup that overlap with res' timeslot
        const // the length we need to find available, plus the required buffers
            l = res.Length.Num(),
            tbuf = berth.TimeBuffer(),
            lbuf = berth.LengthBuffer.Num(),
            // the lowest we could possibly put pm: either end of berth, or furthest possible given pier length
            bottom = Math.min(berth.PierMark.Num() + berth.Length.Num(), pg.PierGroup.Length.Num() + pg.PierGroup.EndOverhang.Num() - l),
            overlaps = pg.TimelineItems()
                .map(i => i.TimelinePosition())
                .filter(pos => (pos.Start() && pos.End() && pos.Length()) !== null
                    && (+pos.End()! + tbuf) > +pt.Arrival.Effective() 
                    && (+pos.Start()! - tbuf) < +pt.Departure.Effective()
                ).map(pos => ({ 
                    top: pos.PierMark.Num() - lbuf, 
                    bottom: pos.PierMark.Num() + pos.Length()!.Num() + lbuf
                })),
            // find candidate piermarks for res: either at the top of the berth or right under an existing reservation in the berth
            pms = [berth.PierMark.Num()].concat(overlaps
                    .filter(o => o.bottom > berth!.PierMark.Num() && o.bottom <= bottom)
                    .map(o => o.bottom));

        pms.sort((a, b) => a - b);

        // loop through pm candidates until we find one that doesn't overlap an existing reservation
        PM:
        for (let i = 0; i < pms.length; i++) {
            let pm = pms[i];
            // loop through all potential overlaps to see if they do indeed overlap with res
            for (let j = 0; j < overlaps.length; j++) {
                let o = overlaps[j];
                // if o overlaps with the current pm, then pm is invalid, so go to the next candidate
                if (pm < o.bottom && (pm + l) > o.top) {
                    continue PM;
                }
            }
            
            // if we made it here, then the current fm doesn't overlap with any other reservation, so we're done searching
            res.PierMark.Num(pm);

            return this;
        }

        // all candidates failed - set fm to the top of the berth and let the user resolve
        res.PierMark.Num(berth.PierMark.Num());

        return this;
    }

    TimelineItemClick() { return this; }

    OnDateDiscrepancy() { this.AttentionGrabber.Activate(); return this; }

    Confirm() {
        this.Reservation.CommitChanges().then(() => {
            this.changeTool.ReservationDetails(this.Reservation.Reservation);
        });
    }

    Cancel() {
        this.Reservation.RevertChanges();
        this.changeTool.ReservationDetails(this.Reservation.Reservation);
    }
}

export class AttentionGrabber {
    readonly Active : () => boolean;
    private readonly _Active : DataSignal<boolean>;
    private timeoutId : number | undefined;

    constructor() {
        this._Active = S.value(false);
        this.Active = () => this._Active();
        this.timeoutId = undefined;
    }

    Activate() {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => this._Active(false), 200);
        this._Active(true);
    }
}

export class AddShiftTool implements ITimelineItemTool {
    readonly PreviousReservation : BerthReservation;
    readonly NewReservation : BerthReservation;
    readonly Dragging = S.value(true);
    readonly PierGroup : DataSignal<PierGroup>;
    readonly PierMark : Measurement;
    readonly Validation : ValidationWatcher;
    readonly AttentionGrabber = new AttentionGrabber();
    private readonly _Submitting : DataSignal<boolean>;
    readonly Submitting : () => boolean;
    private readonly client : IAjaxClient<BerthReservation, DataCacheUpdateEntities>;
    private readonly minDeparture : TZDate;

    constructor(
        public readonly PortCall : PortCall, 
        private readonly changeTool : IChangeTool,
        private readonly reservationSets : SDataArray<() => BerthReservation[]>, 
        ajax : IAjax, 
        entrypoints : Entrypoints,
        private readonly tzutils : TZUtils
    ) {
        let previous : BerthReservation | null = null; 
        for (let r of PortCall.ActiveReservations()) {
            let dep = r.Itinerary() && r.Itinerary()!.Departure.Effective();
            if (dep && (!previous || dep > previous.Itinerary()!.Departure.Effective())) 
                previous = r;
        }
        this.PreviousReservation = previous!;
        this.NewReservation = new BerthReservation({
            Id: 0,
            SideTo: VesselSide.TBD
        });
        const initArrival = previous!.Itinerary()!.Departure.Effective();
        this.minDeparture = tzutils.newDate(+initArrival + DEFAULT_NEW_SHIFT_DURATION_MINS * 60 * 1000);
        const itinerary = new ItineraryPoint({
            Id: 0,
            PortCallId: PortCall.Id(),
            Arrival: { Estimated: initArrival.toJSON() },
            Departure: { Estimated: this.minDeparture.toJSON() }
        });
        this.NewReservation.Itinerary = () => itinerary;
        this.PierGroup = S.value(previous!.Berth()!.PierGroup()!);
        this.PierMark = new Measurement({ Num: previous!.PierMark.Num(), UOM: previous!.PierMark.UOM() });

        this._Submitting = S.value(false);
        this.Submitting = () => this._Submitting();
        this.client = ajax.Url<BerthReservation, DataCacheUpdateEntities>(entrypoints.AddShift, { include: [r => r.Itinerary], useCache: true }),
        // NOTE: This subscribes to this.client.LastResponse, but that's not a memory leak b/c this.client will be
        // freed for GC when this AddShiftTool is.
        this.Validation = ValidationWatcher(this.client);

        // Set NewReservation's length for accurate client-side feedback. The server will apply it's own version
        // of the same logic when we submit our AddShift request.
        // NOTE: We're fine subscribing to this.NewReservation.SideTo b/c this.NewReservation will be freed for
        // GC when this AddShiftTool is, but we avoid subscribing to any properties of PortCall to avoid creating
        // a memory leak.
        S.on(this.NewReservation.SideTo, () => {
            const length = BerthReservation.calculateLength(
                PortCall.Vessel()?.Length ?? null,
                PortCall.Vessel()?.Beam ?? null,
                PortCall.LengthOnlyLength,
                this.NewReservation.SideTo(),
                PortCall.LengthOnlyLength.UOM() ?? UOM.Foot
            );
            if (length?.Num()) this.NewReservation.Length.set(length as Measurement);
        });
    }

    get Departure() {
        return this.NewReservation.Itinerary()!.Departure.Estimated;
    }

    get Arrival() {
        return this.NewReservation.Itinerary()!.Arrival.Estimated;
    }

    TimelinePosition() {
        return {
            Start: this.Arrival,
            // Allow shifting date range to be in the future when in dragging mode, as user might be trying to
            // create a longer shift.
            End: () => this.Dragging() ? VERY_LONG_TIME_IN_THE_FUTURE : maxDate(this.Arrival(), this.Departure()),
            Berth: () => berthAtPierMark(this.PierGroup(), this.PierMark.Num()),
            PierMark: this.PierMark,
            Length: () => this.NewReservation.Length,
            UniqueKey: () => 'add-shift-tool'
        };
    }

    Drag(loc : GridLocation | null) {
        if (this.Dragging() && loc && loc.PierGroup.Port() === this.PortCall.Port()) {
            this.PierGroup(loc.PierGroup);
            this.PierMark.Num(roundPierMark(loc.PierMark.Num() - this.NewReservation.Length.Num() / 2));
            this.Departure(roundTime(+loc.Date, this.tzutils) < this.minDeparture ? this.minDeparture : roundTime(+loc.Date, this.tzutils));
        }
    }

    OpenSpaceClick() {
        if (this.Dragging()) {
            const
                prev = this.PreviousReservation,
                // TODO: It'd be slick to figure out if this "shift" is really the in (or out) movement from
                // (or to) an anchorage, in which case using the arrival (or departure) berth's
                // BerthTransitOffset would probably make more sense here.
                transitMins = prev.Berth()!.PierGroup() === this.PierGroup() ? SAME_PIERGROUP_TRANSIT_MINS : DIFFERENT_PIERGROUP_TRANSIT_MINS,
                arrival = tzmomentMod(this.tzutils, prev.Itinerary()!.Departure.Effective(), d => d.add(transitMins, "minutes")),
                minDeparture = tzmomentMod(this.tzutils, arrival, d => d.add(DEFAULT_NEW_SHIFT_DURATION_MINS, "minutes"));

            this.Arrival(arrival);

            if (this.Departure() < minDeparture) this.Departure(minDeparture);

            this.Dragging(false);
        }

        return this;
    }

    TimelineItemClick() { return this; }

    OnDateDiscrepancy() { this.AttentionGrabber.Activate(); return this; }

    Confirm() {
        if (this._Submitting()) return;
        this._Submitting(true);

        const berth = berthAtPierMark(this.PierGroup(), this.PierMark.Num())!;

        this.NewReservation.Footmark.Num(FootmarkForPierMark(berth, this.PierMark.Num()));
        this.NewReservation.Footmark.UOM(berth.StartMark.UOM());
        this.NewReservation.Itinerary()!.LocationId(berth.Id());

        this.client.Post(this.NewReservation).then(entities => {
            this.reservationSets.push(entities.BerthReservations);

            let res = entities.BerthReservations()[0];

            this.changeTool.ReservationDetails(res);
        }).finally(() => this._Submitting(false));
    }

    Cancel() {
        this.changeTool.None();
    }
}

export class ReservationDetailsTool implements ITimelineItemTool {
    constructor(
        readonly Reservation : BerthReservationModel,
        private readonly changeTool : IChangeTool
    ) { }

    get TimelineItem() { return this.Reservation; }
    get Validation() { return this.Reservation.Validation; }

    TimelineItemClick(item : TimelineItem) {
        return item === this.Reservation ? null : item.MakeDetailsTool(this.changeTool);
    }

    LogActual(arrival : boolean) {
        let pt = this.Reservation.Reservation.Itinerary();

        if (!pt) return;

        let sched = arrival ? pt.Arrival : pt.Departure;

        S.freeze(() => {
            sched.Actual(sched.Estimated());
            this.EditTimeblock();
        });
    }

    EditTimeblock() {
        this.changeTool.EditTimeblock(this.Reservation.Reservation);
    }

    ChangeBerth() {
        this.changeTool.ChangeBerth(this.Reservation.Reservation);
    }

    AddShift() {
        this.changeTool.AddShift(this.Reservation.Reservation);
    }

    ShiftReservation(r : BerthReservation) {
        this.changeTool.ReservationDetails(r);
    }

    ViewOverlaps() {
        this.changeTool.ReservationOverlaps(this.Reservation.Reservation);
    }
}

export class UnlistedReservationDetailsTool implements ITimelineItemTool {
    constructor(
        readonly UnlistedReservation : UnlistedReservationModel,
        private readonly changeTool : IChangeTool
    ) { }

    get TimelineItem() { return this.UnlistedReservation; }

    TimelineItemClick(item : TimelineItem) {
        return item === this.UnlistedReservation ? null : item.MakeDetailsTool(this.changeTool);
    }
}

export class BerthClosureDetailsTool implements ITimelineItemTool {
    readonly ShowActionsMenu = S.value(false);
    readonly ShowConfirmDelete = S.value(false);
    readonly AttentionGrabber = new AttentionGrabber();

    constructor(
        readonly BerthClosure : BerthClosureModel,
        private readonly changeTool : IChangeTool
    ) { }

    get TimelineItem() { return this.BerthClosure; }

    // DON'T proxy this.BerthClosure.Validation via this.Validation.
    // ITimelineItemTool.Validation opens a modal popup on validation errors, which is a good
    // UX for server-side validation errors but works really badly for client-side validation
    // like we do for berth closures. We'll rely on inline validation messages in the berth
    // closure details popup instead.

    OpenSpaceClick() { return this.BerthClosure.HasUnsavedChanges() ? this : null; }

    TimelineItemClick(item : TimelineItem) {
        return this.BerthClosure.HasUnsavedChanges() ? this : item === this.BerthClosure ? null : item.MakeDetailsTool(this.changeTool);
    }

    OnDateDiscrepancy() {
        if (this.BerthClosure.HasUnsavedChanges()) {
            this.AttentionGrabber.Activate();
            return this;
        } else {
            return null;
        }
    }

    ToggleActionsMenu() {
        this.ShowActionsMenu(!this.ShowActionsMenu());
    }

    MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            this.ShowActionsMenu(false);
        });
    }

    DeleteClosure() {
        this.BerthClosure.Delete().then(() => this.changeTool.None());
    }

    ViewOverlaps() {
        this.changeTool.BerthClosureOverlaps(this.BerthClosure.BerthClosure);
    }
}

export class ReservationOverlapsTool implements ITimelineItemTool {
    private readonly navigation : OverlapsNavigation;

    constructor(
        readonly Reservation : BerthReservationModel,
        readonly Now : () => TZDate,
        data : PCCache,
        changeTool : IChangeTool
    ) {
        this.navigation = new OverlapsNavigation(data, changeTool);
    }

    get TimelineItem() { return this.Reservation; }
    get Validation() { return this.Reservation.Validation; }

    ViewConflictingReservation(resId : number) {
        this.navigation.ViewConflictingReservation(resId);
    }

    ViewConflictingBerthClosure(bclId : number) {
        this.navigation.ViewConflictingBerthClosure(bclId);
    }
}

export class BerthClosureOverlapsTool implements ITimelineItemTool {
    private readonly navigation : OverlapsNavigation;

    constructor(
        readonly BerthClosure : BerthClosureModel,
        readonly Now : () => TZDate,
        data : PCCache,
        changeTool : IChangeTool
    ) {
        this.navigation = new OverlapsNavigation(data, changeTool);
    }

    get TimelineItem() { return this.BerthClosure; }

    // DON'T proxy this.BerthClosure.Validation via this.Validation, for the same reasons as
    // BerthClosureDetailsTool above.

    ViewConflictingReservation(resId : number) {
        this.navigation.ViewConflictingReservation(resId);
    }

    ViewConflictingBerthClosure(bclId : number) {
        this.navigation.ViewConflictingBerthClosure(bclId);
    }
}

class OverlapsNavigation {
    constructor(
        private readonly data : PCCache,
        private readonly changeTool : IChangeTool
    ) { }

    ViewConflictingReservation(resId : number) {
        // Our issue schema will tell us if something has an issue w/ a berth reservation,
        // but not whether that reservation shows up as listed or unlisted (since that's
        // permissions dependent). So try looking up the reservation for the given ID, and
        // if that fails, try looking up an unlisted reservation for the same ID.
        const r = this.data.BerthReservations.index(resId)();
        if (r) {
            this.changeTool.ReservationDetails(r);
        } else {
            const ur = this.data.UnlistedBerthReservations.index(resId)();
            if (ur) {
                this.changeTool.UnlistedReservationDetails(ur);
            } else {
                throw new Error(`No BerthReservation or UnlistedBerthReservation found with ID ${resId}`);
            }
        }
    }

    ViewConflictingBerthClosure(bclId : number) {
        const bcl = this.data.BerthClosures.index(bclId)();
        if (bcl) {
            this.changeTool.BerthClosureDetails(bcl);
        } else {
            throw new Error(`No BerthClosure found with ID ${bclId}`);
        }
    }
}

// must move an appreciable ammount to trigger change: moved flag keeps track of that
const dragReservationThreshhold = 5,
    minOverlap = 50; // minimum length of reservation that must not be overhanging

export class EditTimeblockTool implements ITimelineItemTool {
    readonly AttentionGrabber = new AttentionGrabber();

    constructor(
        readonly Reservation : BerthReservationModel,
        private readonly changeTool : IChangeTool,
        private readonly tzutils : TZUtils
    ) { }

    get TimelineItem() { return this.Reservation; }
    get Validation() { return this.Reservation.Validation; }

    OpenSpaceClick() { return this; }

    TimelineItemClick() { return this; }

    OnDateDiscrepancy() { this.AttentionGrabber.Activate(); return this; }

    StartDragReservation(start : GridLocation, compact : boolean) {
        let b = this.Reservation.Reservation.Berth()!;
        const res = this.Reservation.Reservation,
            pt = this.Reservation.ItineraryPoint,
            pg = b && b.PierGroup()!;

            // we "box" how far the reservation can be dragged to only within the current piergroup
        const markbox = {
                min: Math.max(-pg.StartOverhang.Num(), minOverlap - res.Length.Num()),
                max: pg.Length.Num() + Math.min(pg.EndOverhang.Num(), res.Length.Num() - minOverlap) - res.Length.Num()
            },
            startArrival = pt.Arrival.Effective(),
            startDeparture = pt.Departure.Effective(),
            duration = +startDeparture - +startArrival,
            startPierMark = res.PierMark.Num(),
            startBerth = b;

        let moved = '' as '' | 'x' | 'y';

        return (loc : GridLocation) => {
            S.freeze(() => {
                const dPierMark = loc.PierMark.Num() - start.PierMark.Num(),
                    dMilliseconds = +loc.Date - +start.Date,
                    dX = loc.X - start.X,
                    dY = loc.Y - start.Y;

                // set moved flag if we've moved at least 5 px in x or y direction
                moved = 
                    Math.abs(dX) > dragReservationThreshhold && (compact || Math.abs(dX) > Math.abs(dY)) ? 'x' :
                    Math.abs(dY) > dragReservationThreshhold && (!compact && Math.abs(dY) > Math.abs(dX)) ? 'y' :
                    '';

                if (moved === 'x') {
                    const newArrivalTime = roundTime(+startArrival + dMilliseconds, this.tzutils),
                        newDepartureTime = this.tzutils.newDate(+newArrivalTime + duration);
                    
                    pt.Arrival.Effective(newArrivalTime);
                    pt.Departure.Effective(newDepartureTime);

                    // since we're only dragging in x direction, reset y to start
                    pt.LocationId(startBerth.Id());
                    res.PierMark.Num(startPierMark);
                } else if (moved === 'y') {
                    // ignore drag motions beyond piergroup
                    if (loc.PierGroup !== pg) return;

                    const newPierMark = box(markbox, roundPierMark(startPierMark + dPierMark));
                    //check the new piermark to see if it falls in the same berth or a different one in the pier group
                    if (newPierMark < b.PierMark.Num() || newPierMark > b.PierMark.Num() + b.Length.Num()) {
                        const berths = pg.Berths();
                        for (let i = 0; i < berths.length; i++) {
                            const berth = berths[i];
                            if (newPierMark >= berth.PierMark.Num() && newPierMark <= berth.PierMark.Num() + berth.Length.Num()) {
                                b = berth;
                                pt.LocationId(b.Id());
                                break;
                            }
                        }
                    }

                    res.PierMark.Num(newPierMark);

                    // reset any changes to x direction
                    pt.Arrival.Effective(startArrival);
                    pt.Departure.Effective(startDeparture);
                }
            });
        }
    }

    DragDate(arrival : boolean) {
        return (loc : GridLocation) => {
            let pt = this.Reservation.Reservation.Itinerary();

            if (!pt) return;

            let date = roundTime(+loc.Date, this.tzutils);

            // can't drag date beyond opposite line time
            if (arrival) {
                if (+date > +pt.Departure.Effective()) date = pt.Departure.Effective();
                pt.Arrival.Effective(date);
            } else {
                if (+date < +pt.Arrival.Effective()) date = pt.Arrival.Effective();
                pt.Departure.Effective(date);
            }
        }
    }

    LogActual(arrival : boolean) {
        let pt = this.Reservation.Reservation.Itinerary();

        if (!pt) return;

        let sched = arrival ? pt.Arrival : pt.Departure;

        sched.Actual(sched.Estimated());
    }

    Confirm() {
        this.Reservation.CommitChanges().then(() => {
            this.changeTool.ReservationDetails(this.Reservation.Reservation);
        });
    }

    Cancel() {
        this.Reservation.RevertChanges();
        this.changeTool.ReservationDetails(this.Reservation.Reservation);
    }
}

function FootmarkForPierMark(b : Berth, pm : number) {
    return pm - b.PierMark.Num() + b.StartMark.Num();
}

export interface GridLocation {
    PierGroup : PierGroup;
    PierMark : Measurement;
    Date : TZDate;
    X : number;
    Y : number;
}

export interface GridRegion {
    PierGroup : PierGroup;
    PierMark : number;
    Length : number;
    First : TZDate;
    Last : TZDate;
}

export class NewReservationTool implements ITimelineItemTool {
    readonly PierGroup : () => PierGroup;
    readonly PierMark : Measurement;
    readonly Length : Measurement;
    readonly Arrival : DataSignal<TZDate>;
    readonly Departure : DataSignal<TZDate>;

    constructor(loc : GridRegion, private readonly tzutils : TZUtils, private readonly piergroupMap : (pg : PierGroup) => PierGroupModel) {
        const uom = S.sample(loc.PierGroup.Length.UOM);
        this.PierGroup = () => loc.PierGroup;
        this.PierMark = new Measurement({ Num: loc.PierMark, UOM: uom });
        this.Length = new Measurement({ Num: loc.Length, UOM: uom });
        this.Arrival = S.value(loc.First, TZDate.eq);
        this.Departure = S.value(loc.Last, TZDate.eq);
    }

    TimelinePosition() {
        return {
            Start: this.Arrival,
            End: () => maxDate(this.Arrival(), this.Departure()),
            Berth: () => berthAtPierMark(this.PierGroup(), this.PierMark.Num()),
            PierMark: this.PierMark,
            Length: () => this.Length,
            UniqueKey: () => 'new-reservation-tool'
        };
    }

    static TryCreateTool(loc : GridLocation, berthRights : { [ id : number ] : BerthRights }, tzutils : TZUtils, piergroupMap : (pg : PierGroup) => PierGroupModel) {
        var region = NewReservationTool.FindReservableRegion(loc, berthRights, tzutils, piergroupMap);
        return region && new NewReservationTool(region, tzutils, piergroupMap);
    }

    // based on the location of the click event, see how much of the pier group is available for reservation, 
    // up to the entire piergroup for the entire day of the click
    // returns either a NewReservation object or null, if no space was available.
    static FindReservableRegion(loc : GridLocation, berthRights : { [ id : number ] : BerthRights }, tzutils : TZUtils, piergroupMap : (pg : PierGroup) => PierGroupModel) : GridRegion | null {
        const
            pg = piergroupMap(loc.PierGroup),
            cpm = loc.PierMark.Num(),
            day = loc.Date;

        // start out trying to reserve entire pier group, from piermark (pm) 0 to full length (len)
        let pm = 0,
            len = pg.PierGroup.Length.Num(),
            // start with duration of (almost) a full day
            first = tzmomentMod(tzutils, day, m => m.startOf('day')),
            fail = false;

        let last = tzmomentMod(tzutils, first, m => m.add(1, 'day').subtract(1, 'minute'));

        // dimensions of timeline items, padded by required buffers
        const others = pg.TimelineItems()
            .map(i => i.TimelinePosition())
            .filter(pos => (pos.Start() && pos.End() && pos.Berth() && pos.Length()) !== null)
            .map(pos => {
                var pbuf = pos.Berth()!.LengthBuffer.Num(),
                    tbuf = pos.Berth()!.TimeBuffer() * 60 * 1000;
                return {
                    top: pos.PierMark.Num() - pbuf,
                    bot: pos.PierMark.Num() + pos.Length()!.Num() + pbuf,
                    // TH 03/29/2017 -- NOTE: Before refactoring to use an ITimelinePosition,
                    // this code used Itinerary()!.Arrival.Estimated() and Itinerary()!.Departure.Estimated.
                    // The switch to ITimelinePositions has the functional effect of using Effective instead of Estimated.
                    // This was the only spot on the page we were using Estimates instead of Effectives in overlap calculations.
                    // From talking w/ AH, we believe Effective times should work just as well here.
                    first: pos.Start()!.getTime() - tbuf,
                    last: pos.End()!.getTime() + tbuf
                };
            });

        // if user doesn't have rights to reserve any berths in the piergroup, block them out
        pg.Berths().forEach(function (b) {
            if (!(berthRights[S.sample(b.Id)] & BerthRights.Reserve)) {
                others.push({
                    top: b.PierMark.Num(),
                    bot: b.PierMark.Num() + b.Length.Num(),
                    first: +first,
                    last: +last
                });
            }
        });

        // now we search to see how much of the day is available
        others.forEach(function (r) {
            // see if reservation is in line with click point
            if (r.top < cpm && cpm < r.bot) {
                // see if other res is before, after, or at (!) our click
                if (r.last < +day) {
                    // before.  check if we need to pull back our start time
                    if (r.last > +first) first = tzutils.newDate(r.last);
                } else if (+day < r.first) {
                    // after.  check if we need to bring back our end time
                    if (r.first < +last) last = tzutils.newDate(r.first);
                } else {
                    // click is *on* reservation - can't make res
                    fail = true;
                }
            }
        });

        if (fail) return null;

        // now that we know our time range, see how much of the piergroup length is open
        others.forEach(function (r) {
            // see if other res overlaps our time range
            if (r.last > +first && r.first < +last) {
                // yes: check if it overlaps our pier range
                if (cpm < r.top) {
                    // clicked above other res and other res overlaps: shorten length of new res
                    if (pm + len > r.top) len = r.top - pm;
                } else if (cpm > r.bot) {
                    // clicked below other res and other res overlaps: drop footmark and shorten equiv. ammount
                    if (r.bot > pm) {
                        len -= (r.bot - pm);
                        pm = r.bot;
                    }
                } else {
                    // click is *on* reservation - can't make res
                    fail = true;
                }
            }
        });
        
        if (fail) return null;

        return { PierGroup: pg.PierGroup, PierMark: pm, Length: len, First: first, Last: last };
    }

    StartDateDrag(arrival : boolean) {
        const
            date = arrival ? this.Arrival : this.Departure,
            opposite = arrival ? this.Departure : this.Arrival,
            dir = date() < opposite() ? -1 : 1, // are we dragging the left handle (dir = -1) or the right one (dir = 1)?
            pmBox = { min: 0, max: this.PierGroup().Length.Num() },
            dateBox = { min: dir * +opposite() },
            barriers = this.piergroupMap(this.PierGroup()).TimelineItems()
                .map(i => i.TimelinePosition())
                .filter(pos => (pos.Start() && pos.End() && pos.Berth() && pos.Length()) !== null)
                .map(pos => {
                    let b = pos.Berth()!,
                        tbuf = b.TimeBuffer() * 60 * 1000,
                        pbuf = b.LengthBuffer.Num(),
                        d = +pos.Start()! - tbuf < +opposite() && +opposite() < +pos.End()! + tbuf
                            ? dateBox.min
                            : dir === 1 ? +pos.Start()! - tbuf
                                        : dir * +pos.End()! - tbuf;
                    return {
                        date: d,
                        top: pos.PierMark.Num() - pbuf,
                        bottom: pos.PierMark.Num() + pos.Length()!.Num() + pbuf
                    };
                })
                .filter(r => r.date >= dateBox.min);

        barriers.sort((a, b) => a.date - b.date);

        let lastValidPm = pmBox.min;

        const checkfit = (d : number, pm : number) => {
            var top = pmBox.min,
                bottom = pmBox.max,
                i : number;

            for (i = 0; i < barriers.length; i++) {
                let barrier = barriers[i];
                if (barrier.date > d) break;
                if (pm < barrier.top) {
                    // cutting above barrier
                    if (bottom > barrier.top) {
                        bottom = barrier.top;
                    }
                } else if (pm > barrier.bottom) {
                    // cutting below barrier
                    if (top < barrier.bottom) {
                        top = barrier.bottom;
                    }
                } else {
                    // ran straight into barrier - halt
                    d = barrier.date;
                    break;
                }
            }

            if (d <= dateBox.min) {
                // impossible, ignore action;
                return;
            }

            // record the last valid piermark in case user moves mouse to impossible position
            lastValidPm = pm;
            
            S.freeze(() => {
                this.PierMark.Num(top);
                this.Length.Num(bottom - top);
                date(roundTime(dir * d, this.tzutils));
            });
        };

        return (loc : GridLocation) => {
            var d = box(dateBox, dir * +loc.Date),
                pm = loc.PierGroup === this.PierGroup() ? box(pmBox, loc.PierMark.Num()) : lastValidPm;

            checkfit(d, pm);
        }
    }

    Reserve() {
        const berth = berthAtPierMark(this.PierGroup(), this.PierMark.Num())

        if (!berth) return;

        window.location.href = '#!addportcall?berth=' + berth.Id()
            + '&footmark=' + Math.ceil(FootmarkForPierMark(berth, this.PierMark.Num())) // round to nearest whole number
            + '&firstline=' + encodeURIComponent(this.Arrival().toJSON())
            + '&lastline=' + encodeURIComponent(this.Departure().toJSON());
    }
}

const typeaheadOptions = {
    minLength: 0,
    highlight: true,
    hint: false
};

export type PortFilterModel = ReturnType<typeof PortFilterModel>;
function PortFilterModel(params : ScheduleGridParams, ports : () => PortModel[]) {
    // filter values are route parameters, and baring that, we preserve them in localStorage
    var Query = S.value(params.port),
        debouncedQuery = debounceSignal(Query);

    const typeahead = new Typeahead<PortModel>({
        typeaheadOptions: typeaheadOptions,
        collection: ports,
        observable: Query,
        matchFn: function (port : PortModel, query : string) { return CaseInsensitiveContains(port.Port.Name(), query); },
        displayKey: function (port : PortModel) { return port.Port.Name(); }
    });

    return {
        Query,
        QueryMatches: (port : Port) => !debouncedQuery() || CaseInsensitiveContains(port.Name(), debouncedQuery()),
        Typeahead: typeahead,
        ShowFilter: () => ports().length > 1
    };
}

export type CapabilityFilterModel = ReturnType<typeof CapabilityFilterModel>;
function CapabilityFilterModel(params : ScheduleGridParams, berthCapabilities: ArraySignal<BerthCapability>) {
    // filter values are route parameters, and baring that, we preserve them in localStorage
    var Query = S.value(params.capability),
        debouncedQuery = debounceSignal(Query);

    const typeahead = new Typeahead<BerthCapability>({
        typeaheadOptions: typeaheadOptions,
        collection: berthCapabilities.filter(c => c.Active()),
        observable: Query,
        matchFn: function (cap, query) { return CaseInsensitiveContains(cap.Name(), query); },
        displayKey: function (cap) { return cap.Name(); }
    });

    return {
        Query,
        QueryMatches,
        Typeahead: typeahead
    };

    function QueryMatches(pg : PierGroup) {
        return !debouncedQuery() || pg.Berths().some(berth =>
            berth.Capabilities().some(bca =>
                !!bca.Capability() && CaseInsensitiveContains(bca.Capability()!.Name(), debouncedQuery())
            )
        );
    }
}

function roundTime(t : number, tzutils : TZUtils) {
    return tzutils.newDate(nearest(timeIncrement * 60 * 1000, t));
}

function roundPierMark(d : number) {
    return nearest(pierMarkIncrement, d);
}

function box(b : { min? : number, max? : number }, x : number) {
    return b.min !== undefined && x < b.min ? b.min : b.max !== undefined && x > b.max ? b.max : x;
}

function nearest(n : number, x : number) {
    return n * Math.round(x / n);
}

// Helper function that we call above to get unique IDs from lists of BerthReservations and
// other "timeline item" entities and filter out deleted entities.
function uniqueIds(sets : () => (() => { Id : () => number }[])[], index : (id: number) => () => object | null) {
    var ids = {} as { [ id : number ] : boolean };
    sets().forEach(set => set().forEach(item => {
        var id = item.Id();
        if (index(id)() !== null) {
            ids[id] = true;
        }
    }));
    return Object.keys(ids).map(id => +id);
}

export type ScheduleGrid = ReturnType<typeof ScheduleGrid>;


// WEBPACK FOOTER //
// ./apps/portcall/schedulegrid/model.ts